import { Campaign, Collab, formatPostByDate, goLiveWindowFor, postingDayBounds } from '@foodfluence/shared';
import { toDate } from './dateUtils';
import { calendarDayInTimezone, getBrowserTimezone } from './timezoneUtils';
import { getContentDueDateRaw } from '../features/collabs/utils/dateUtils';
import { isShipToHomeCampaignType, isInStoreCampaignType, isRetailCampaignType, isShopifyCampaignType, getEffectiveEndDate } from './collabUtils';
import {
  IN_STORE_TURNAROUND_DAYS,
  RETAIL_TURNAROUND_DAYS,
  SHOPIFY_TURNAROUND_DAYS,
} from '../constants/turnaroundConstants';

/**
 * Helper function to convert Firebase timestamp to Date
 */
const convertFirebaseTimestampToDate = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  return null;
};

/** 'YYYY-MM-DD' -> local midnight of that calendar day (what the date picker compares). */
const ymdToLocalDay = (ymd: string): Date => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(y, m - 1, d);
};

/**
 * The go-live posting days as picker dates, or null without go-live: the window as calendar days
 * in `timezone`, or only the day after a draft approved after go-live end (postingDayBounds) —
 * the bounds the server's assertPostingDateWithinDueDate enforces.
 */
export const getGoLivePostingBounds = (
  collab: Collab,
  campaign: Campaign,
  timezone: string = getBrowserTimezone()
): { min: Date; max: Date } | null => {
  const window = goLiveWindowFor(collab, campaign);
  if (!window) return null;
  const { min, max } = postingDayBounds(collab, window, timezone);
  return { min: ymdToLocalDay(min), max: ymdToLocalDay(max) };
};

/**
 * Calculates the maximum allowed posting date for a collab based on campaign type
 *
 * Rules:
 * - In-store collabs: IN_STORE_TURNAROUND_DAYS days after scheduledCollabDate
 * - Retail collabs (before acceptance): RETAIL_TURNAROUND_DAYS from current time
 * - Retail collabs (after acceptance): RETAIL_TURNAROUND_DAYS from collabAcceptedTime
 * - Shopify collabs: SHOPIFY_TURNAROUND_DAYS after orderFulfilledTime
 * - A stored contentDueDate (the backend rejects a posting date past it) wins over all of the
 *   above, which remain only as the legacy fallback for collabs with no stored date. Its cap is
 *   the due date's calendar day in `timezone` (the collab's, see useDueDateTimezone), the day the
 *   backend compares against. A lapsed stored date is not reopened to today: that needs an extension
 * - Always constrained by campaign end date (whichever is earlier)
 */
export const getMaxPostingDate = (
  collab: Collab | null,
  campaign?: Campaign,
  timezone: string = getBrowserTimezone()
): Date | null => {
  if (!collab || !campaign) return null;

  // Go-live: the posting day must fall inside the window, for every type (the due date is the
  // draft / turnaround clock there, so it is no bound). A draft approved after go-live end gets
  // the day after the approval (postingDayBounds). The campaign end is ignored, as on the server.
  const bounds = getGoLivePostingBounds(collab, campaign, timezone);
  if (bounds) return bounds.max;

  const isInStore = isInStoreCampaignType(campaign.campaignType);
  const isRetail = isRetailCampaignType(campaign.campaignType);
  const isShopify = isShopifyCampaignType(campaign.campaignType);

  let postingLimit: Date | null = null;

  const storedDueDate =
    toDate(collab.contentDueDate) ??
    (isShipToHomeCampaignType(campaign.campaignType) ? getContentDueDateRaw(collab, campaign, timezone) : null);

  if (storedDueDate) {
    postingLimit = calendarDayInTimezone(storedDueDate, timezone);
  } else if (isInStore) {
    // In-store: max IN_STORE_TURNAROUND_DAYS days after visit date
    if (!collab.scheduledCollabDate) return null;

    postingLimit = toDate(collab.scheduledCollabDate);
    postingLimit.setHours(0, 0, 0, 0);
    postingLimit.setDate(postingLimit.getDate() + IN_STORE_TURNAROUND_DAYS);
  } else if (isRetail) {
    // Retail: depends on whether collab has been accepted
    const acceptedTime = collab.collabConfirmedTime ? toDate(collab.collabConfirmedTime) : null;
    
    if (acceptedTime) {
      // After acceptance: RETAIL_TURNAROUND_DAYS from acceptance time
      postingLimit = acceptedTime;
      postingLimit.setHours(0, 0, 0, 0);
      postingLimit.setDate(postingLimit.getDate() + RETAIL_TURNAROUND_DAYS);
    } else {
      // Before acceptance: RETAIL_TURNAROUND_DAYS from now
      postingLimit = new Date();
      postingLimit.setHours(0, 0, 0, 0);
      postingLimit.setDate(postingLimit.getDate() + RETAIL_TURNAROUND_DAYS);
    }
  } else if (isShopify) {
    // Shopify: SHOPIFY_TURNAROUND_DAYS after order fulfilled
    if (!collab.orderFulfilledTime) return null;

    postingLimit = toDate(collab.orderFulfilledTime);
    postingLimit.setHours(0, 0, 0, 0);
    postingLimit.setDate(postingLimit.getDate() + SHOPIFY_TURNAROUND_DAYS);
  }

  // Get campaign end date
  const effectiveEndDate = getEffectiveEndDate(campaign, collab);
  const campaignEndDate = effectiveEndDate ? convertFirebaseTimestampToDate(effectiveEndDate) : null;

  // Return the earlier of posting limit and campaign end date
  const limit = !postingLimit
    ? campaignEndDate
    : !campaignEndDate
      ? postingLimit
      : postingLimit < campaignEndDate ? postingLimit : campaignEndDate;

  // Never behind the minimum. A retail turnaround runs from acceptance, so a creator who comes
  // back after it lapses gets a maximum EARLIER than the minimum, and DateSelector then disables
  // every day on the calendar — no date can be picked, the redeem step can never be completed,
  // and the collab is stuck at pendingRedemption for good. The brand has already been charged by
  // then, so reopening today is the lesser of the two: a late post beats content never delivered.
  //
  // The campaign's own end date is the one ceiling this will not lift. Reopening past it would
  // schedule a post for a campaign that is over — after the window the brand agreed to and reports
  // on — which is a different problem from the one above, not a smaller version of it. A collab
  // caught between a lapsed turnaround and an ended campaign has nothing valid left to pick, and
  // belongs in an extension request rather than a calendar.
  //
  // Nor a stored due date: assertPostingDateWithinDueDate refuses any day after it, so reopening
  // today would offer a date the save then rejects. A lapsed stored date needs an extension.
  const min = getMinPostingDate(collab, campaign);
  const campaignEndPassed = campaignEndDate !== null && campaignEndDate < min;
  if (limit && limit < min && !campaignEndPassed && !toDate(collab.contentDueDate)) return min;

  return limit;
};

/**
 * Calculates the minimum allowed posting date for a collab
 * 
 * Rules:
 * - In-store collabs: day after scheduledCollabDate (must visit first)
 * - Shopify collabs: orderFulfilledTime (can post once order is fulfilled)
 * - Retail collabs: current date (can post anytime)
 * - Other types: current date
 */
export const getMinPostingDate = (
  collab: Collab | null,
  campaign?: Campaign,
  timezone: string = getBrowserTimezone()
): Date => {
  const usual = getUsualMinPostingDate(collab, campaign);
  // Go-live: also never before the window opens (or the late-approval day).
  const bounds = collab && campaign ? getGoLivePostingBounds(collab, campaign, timezone) : null;
  if (!bounds) return usual;
  const min = bounds.min > usual ? bounds.min : usual;
  // An in-store visit on the window's last day still leaves that day to post on.
  const visit = isInStoreCampaignType(campaign?.campaignType) && collab?.scheduledCollabDate ? toDate(collab.scheduledCollabDate) : null;
  if (visit && min > bounds.max && new Date(visit).setHours(0, 0, 0, 0) === bounds.max.getTime()) return bounds.max;
  return min;
};

const getUsualMinPostingDate = (collab: Collab | null, campaign?: Campaign): Date => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (!collab || !campaign) return today;

  const isInStore = isInStoreCampaignType(campaign.campaignType);
  const isShopify = isShopifyCampaignType(campaign.campaignType);

  if (isInStore && collab.scheduledCollabDate) {
    // In-store: earliest is day after visit
    const minDate = toDate(collab.scheduledCollabDate);
    minDate.setHours(0, 0, 0, 0);
    minDate.setDate(minDate.getDate() + 1);
    return minDate
  }

  if (isShopify && collab.orderFulfilledTime) {
    // Shopify: earliest is when order is fulfilled
    const minDate = toDate(collab.orderFulfilledTime);
    minDate.setHours(0, 0, 0, 0);
    return minDate;
  }

  return today;
};
/**
 * Go-live: the last posting day ('YYYY-MM-DD') once it has passed and the picker has nothing left
 * (min after max, or today after max: e.g. the one late-approval day is over), else null. The bounds stay as the server
 * enforces them; the caller shows a message and the extension request instead of a dead calendar.
 */
export const getPassedGoLivePostingDay = (
  collab: Collab,
  campaign: Campaign,
  timezone: string = getBrowserTimezone()
): string | null => {
  const window = goLiveWindowFor(collab, campaign);
  if (!window) return null;
  const { max } = postingDayBounds(collab, window, timezone);
  const lastDay = ymdToLocalDay(max);
  // Today too: a visit/fulfillment-anchored minimum (or the last-day visit exception) never moves past max on its own.
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return getMinPostingDate(collab, campaign, timezone) > lastDay || today > lastDay ? max : null;
};

/** "Your posting day was Sat, Nov 18. Ask the brand for more time." */
export const passedPostingDayMessage = (ymd: string): string =>
  `Your posting day was ${formatPostByDate(ymd, 'long')}. Ask the brand for more time.`;
